import { ShieldAlertIcon } from "lucide-react";
import { useCallback } from "react";
import { T, RecordProps, TLShape, TLAsset, TLBinding, Editor } from "tldraw";

import {
  BlockBody,
  BlockContainer,
  NodeBlockHeader,
  BlockContainerShapeContext,
} from "@/components/canvas-editor/components/Container";
import {
  CanvasPercentageInput,
  CanvasSimpleSelect,
  PercentOperatorSelect,
} from "@/components/canvas-editor/components/Inputs";
import {
  modelProbabilityExplanation,
  NodeDocs,
} from "@/components/canvas-editor/NodeDocs";
import {
  BindableUtil,
  createMigrations,
} from "@/components/canvas-editor/shapes/bindable";
import {
  ToxicityAnalysisShape,
  toxicityAnalysisType,
  comparisonOperators,
  ComparisonOperator,
  ToxicityAnalysisProps,
  toxicityAnalysisCategories,
  ToxicityAnalysisCategory,
  metadataType,
  Dimensions,
} from "@/components/canvas-editor/shapes/shapes";
import { MIN_H, ToolStateNode } from "@/components/canvas-editor/utils";
import { SimpleTooltip } from "@/components/ui/tooltip";

export function isToxicityAnalysisShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is ToxicityAnalysisShape {
  return shape.type === toxicityAnalysisType;
}

export class ToxicityAnalysisTool extends ToolStateNode {
  static override id = toxicityAnalysisType;

  static override uiToolDef = (editor: Editor) => ({
    id: toxicityAnalysisType,
    icon: ShieldAlertIcon,
    label: ToxicityAnalysisShapeUtil.docs.title,
    defaultColor: ToxicityAnalysisShapeUtil.docs.defaultColor,
    onSelect: () => editor.setCurrentTool(toxicityAnalysisType),
    docs: () => (
      <NodeDocs
        editor={editor}
        component={ToxicityAnalysis}
        util={ToxicityAnalysisShapeUtil}
      />
    ),
  });

  override onEnter() {
    this.editor.setCursor({ type: "none", rotation: 0 });
  }

  override onPointerDown() {
    const { currentPagePoint } = this.editor.inputs;

    this.editor.createShape({
      type: toxicityAnalysisType,
      x: currentPagePoint.x,
      y: currentPagePoint.y,
    });

    this.editor.setCurrentTool("select");
  }
}

export class ToxicityAnalysisShapeUtil extends BindableUtil<ToxicityAnalysisShape> {
  static override migrations = createMigrations(toxicityAnalysisType);
  static override type = toxicityAnalysisType;

  static override docs = {
    defaultColor: "red" as const,
    title: "Toxicity Analysis",
    description:
      "Use AI to analyze the toxicity level of the content. This detects different types of toxic content including severe toxicity, obscenity, threats, insults, and identity-based attacks.",
  };

  override getType() {
    return toxicityAnalysisType;
  }

  static override props: RecordProps<ToxicityAnalysisShape> = {
    w: T.number,
    minW: T.number,
    isCollapsed: T.boolean.optional(),
    resultState: T.literalEnum("success", "failure", "idle").optional(),
    isReadOnly: T.boolean.optional(),
    h: T.number,
    toxicityAnalysisCategory: T.literalEnum(...toxicityAnalysisCategories),
    operator: T.literalEnum(...comparisonOperators),
    value: T.or(T.number, T.string),
    metadata: metadataType,
  };

  override getDefaultProps() {
    return {
      w: 360,
      minW: 400,
      h: MIN_H,
      toxicityAnalysisCategory: "Toxic" as const,
      operator: "<=" as const,
      value: 0.5,
      metadata: undefined,
    };
  }

  override component(shape: ToxicityAnalysisShape) {
    const selected = this.editor.getSelectedShapeIds();
    const isSelected = selected.includes(shape.id);

    return (
      <ToxicityAnalysis
        shape={shape}
        updateShape={this.updateProps.bind(this)}
        isBound={this.getBoundToFeed(shape)}
        isSelected={isSelected}
      />
    );
  }
}

function ToxicityAnalysis({
  isBound,
  shape,
  className,
  updateShape,
  isSelected,
}: {
  isSelected: boolean;
  isBound: boolean;
  shape: ToxicityAnalysisShape;
  className?: string;
  updateShape: (
    shape: ToxicityAnalysisShape,
    props: Partial<ToxicityAnalysisProps>,
  ) => void;
}) {
  const onUpdateDimensions = useCallback(
    (dimensions: Pick<Dimensions, "h">) => {
      return updateShape(shape, dimensions);
    },
    [shape, updateShape],
  );

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        resultState={shape.props.resultState}
        pattern={shape.props.metadata?.pattern}
        isBound={isBound}
        className={className}
        variant={
          shape.props.metadata?.color ||
          ToxicityAnalysisShapeUtil.docs.defaultColor
        }
        isSelected={isSelected}
        onUpdateDimensions={onUpdateDimensions}
        dynamicHeight={true}
      >
        <NodeBlockHeader
          props={shape.props}
          docs={ToxicityAnalysisShapeUtil.docs}
        />
        <BlockBody>
          <CanvasSimpleSelect
            aria-label="Toxicity Analysis Category"
            options={toxicityAnalysisCategories}
            value={shape.props.toxicityAnalysisCategory}
            className="w-[160px] shrink-0"
            onValueChange={(v) =>
              updateShape(shape, {
                toxicityAnalysisCategory: v as ToxicityAnalysisCategory,
              })
            }
            tooltip={ToxicityAnalysisShapeUtil.docs.description}
          />
          <PercentOperatorSelect
            value={shape.props.operator}
            onValueChange={(v) =>
              updateShape(shape, { operator: v as ComparisonOperator })
            }
          />
          <SimpleTooltip message={modelProbabilityExplanation}>
            <CanvasPercentageInput
              aria-label="Threshold"
              value={shape.props.value as number}
              className="flex-1"
              placeholder="Threshold"
              onValueChange={(value) => updateShape(shape, { value })}
            />
          </SimpleTooltip>
        </BlockBody>
      </BlockContainer>
    </BlockContainerShapeContext>
  );
}
